<template>
  <div class="notification-container">
    <transition-group name="notification" tag="div">
      <div
        v-for="notification in notifications"
        :key="notification.id"
        :class="['notification', `notification-${notification.type}`]"
        @click="removeNotification(notification.id)"
      >
        <div class="notification-content">
          <div class="notification-icon">
            <span v-if="notification.type === 'success'"><v-icon color="#489969">mdi-check-circle</v-icon></span>
            <span v-else-if="notification.type === 'error'"><v-icon color="red">mdi-alert-circle-outline</v-icon></span>
            <span v-else-if="notification.type === 'warning'">⚠</span>
            <span v-else-if="notification.type === 'info'"><v-icon color="#8585e0">mdi-information</v-icon></span>
            <span v-else>ℹ</span>
          </div>
          <div class="notification-message">
            {{ notification.message }}
          </div>
          <button class="notification-close" @click.stop="removeNotification(notification.id)">
            ×
          </button>
        </div>
      </div>
    </transition-group>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  name: "Notification",
  computed: {
    ...mapGetters({
      isLoading: 'assignments/isLoading',
      notifications: 'notification/notifications',
    }),
  },
  methods: {
    ...mapActions("notification", ["remove"]),
    removeNotification(id) {
      this.remove(id);
      console.log(`Removing notification with id: ${id}`);
      const index = this.notifications.findIndex(n => n.id === id);
      if (index !== -1) {
        this.notifications.splice(index, 1);
      }
    },
  },
  mounted() {
    // Auto-remove notifications after timeout
    this.$watch(
      () => this.notifications,
      (newNotifications) => {
        if (!newNotifications?.length) return;
        
        newNotifications.forEach((notification) => {
          // Skip if notification already has a timeout set
          if (notification._timeoutId) return;
          
          // Make warning notifications persistent
          if (notification.type === 'warning') {
            console.log(`Warning notification ${notification.id} will persist indefinitely`);
            return; // Skip timeout for warning notifications
          }
          
          // Apply timeout for non-warning notifications
          if (notification.timeout && notification.timeout > 0) {
            // Store the timeout ID on the notification object
            notification._timeoutId = window.setTimeout(() => {
              console.log(`Executing timeout for notification ${notification.id}`);
              this.removeNotification(notification.id);
            }, notification.timeout * 1000); // Convert to milliseconds
          }
        });
      },
      { 
        immediate: true,
        deep: true 
      }
    );
  },

  beforeDestroy() {
    // Clean up any remaining timeouts
    if (this.notifications) {
      this.notifications.forEach(notification => {
        if (notification._timeoutId) {
          window.clearTimeout(notification._timeoutId);
        }
      });
    }
  }
};
</script>

<style scoped>
.notification-container {
  position: fixed;
  right: clamp(8px, 2vw, 16px);
  z-index: 10000;
  max-width: clamp(250px, 80vw, 400px);
  bottom: clamp(16px, 4vw, 26px);
  left: clamp(8px, 2vw, auto);
}

.notification {
  background: white;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  margin-bottom: clamp(8px, 2vw, 10px);
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  max-width: 100%;
}

.notification:hover {
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

.notification-success {
  border-left: 4px solid #4caf50;
  background-color: #E6F4EC;
}

.notification-error {
  border-left: 4px solid #f44336;
  background-color: #ffe7e7;
}

.notification-warning {
  border-left: 4px solid #ff9800;
}

.notification-info {
  border-left: 4px solid #8585e0;
  background-color: #e7e7f9;
}

.notification-content {
  display: flex;
  align-items: center; /* ensure all children are vertically centered */
  padding: clamp(10px, 2.5vw, 12px) clamp(12px, 3vw, 16px);
  gap: clamp(8px, 2vw, 12px);
  min-height: 40px; /* ensure enough height for all elements */
}

.notification-icon {
  font-size: clamp(20px, 4vw, 24px); /* match close button size */
  font-weight: bold;
  width: clamp(28px, 6vw, 32px); /* match close button width */
  height: clamp(28px, 6vw, 32px); /* match close button height */
  text-align: center;
  flex-shrink: 0;
  margin: 0; /* remove top margin for perfect centering */
  display: flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
}

.notification-success .notification-icon {
  color: #4caf50;
}

.notification-error .notification-icon {
  color: #f44336;
}

.notification-warning .notification-icon {
  color: #ff9800;
}

.notification-info .notification-icon {
  color: #2196f3;
}

.notification-message {
  flex: 1;
  font-family: Calibre-R;
  font-weight: 400;
  font-size: clamp(13px, 3vw, 14px);
  line-height: 1.4;
  letter-spacing: 0px;
  vertical-align: middle;
  word-wrap: break-word;
  overflow-wrap: break-word;
  min-width: 0;
}

.notification-close {
  background: none;
  border: none;
  font-size: clamp(20px, 4vw, 24px); /* match icon size */
  cursor: pointer;
  color: #000;
  padding: 0; /* remove all padding for perfect alignment */
  margin: 0 0 0 8px; /* only left margin for spacing */
  position: static;
  flex-shrink: 0;
  touch-action: manipulation;
  line-height: 1;
  width: clamp(28px, 6vw, 32px); /* match icon width */
  height: clamp(28px, 6vw, 32px); /* match icon height */
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
  vertical-align: middle;
  transform: translateY(-2px); /* nudge the × up for better alignment */
}

.notification-close:hover {
  color: #333;
  background-color: rgba(0, 0, 0, 0.05);
}

.notification-close:active {
  background-color: rgba(0, 0, 0, 0.1);
}

/* Transition animations */
.notification-enter-active,
.notification-leave-active {
  transition: all 0.3s ease;
}

.notification-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.notification-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

/* Mobile specific styles */
@media (max-width: 480px) {
  .notification-container {
    right: 8px;
    left: 8px;
    bottom: 16px;
    max-width: none;
  }
  
  .notification {
    border-radius: 6px;
  }
  
  .notification-content {
    padding: 12px;
    gap: 10px;
  }
  
  .notification-message {
    font-size: 13px;
    line-height: 1.3;
  }
  
  .notification-close {
    font-size: 18px;
    width: 24px;
    height: 24px;
  }
}

/* Very small screens */
@media (max-width: 320px) {
  .notification-container {
    right: 6px;
    left: 6px;
    bottom: 12px;
  }
  
  .notification-content {
    padding: 10px;
    gap: 8px;
  }
  
  .notification-message {
    font-size: 12px;
  }
  
  .notification-close {
    width: 22px;
    height: 22px;
    font-size: 16px;
  }
}

/* Landscape mobile adjustments */
@media (max-width: 768px) and (orientation: landscape) {
  .notification-container {
    bottom: 12px;
    max-width: 300px;
  }
}
</style>
